import {
    ButtonHTMLAttributes,
    FunctionComponent,
    ReactNode,
    useCallback,
    useState,
} from 'react';

type ButtonVariant = 'contained' | 'outlined' | 'text';
type ButtonColor = 'primary' | 'secondary' | 'error' | 'success' | 'info';
type ButtonSize = 'small' | 'medium';

type ButtonUIProps = {
    variant?: ButtonVariant;
    color?: ButtonColor;
    size?: ButtonSize;
    startIcon?: ReactNode;
    endIcon?: ReactNode;
    fullWidth?: boolean;
    className?: string;
    children: ReactNode;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'>;

/**
 * Button.
 *
 * The prop API is unchanged (MUI's variant × color × size), but the appearance
 * now comes from the design system's `.ds-btn` rather than a local Tailwind
 * class table — so every button in the app picks up the active theme, including
 * the per-theme skins (Tactile's press physics, Liquid Mercury's sheen, Saga
 * Gold's golden confirm).
 *
 * The two axes map onto the design system like this:
 *   contained → `primary`  (solid fill)
 *   outlined  → `outline`  (transparent fill, tinted border + text)
 *   text      → `ghost`    (no fill, no border)
 * and `color` becomes `data-ds-tone`, which only swaps the colour so the
 * geometry and the theme skin still come from the variant.
 */
const VARIANT: Record<ButtonVariant, string> = {
    contained: 'primary',
    outlined: 'outline',
    text: 'ghost',
};

const SIZE: Record<ButtonSize, string> = {
    small: 'sm',
    medium: 'md',
};

const ButtonUI: FunctionComponent<ButtonUIProps> = ({
    variant = 'contained',
    color = 'primary',
    size = 'medium',
    startIcon,
    endIcon,
    fullWidth = false,
    className = '',
    disabled,
    children,
    onKeyDown,
    onKeyUp,
    onBlur,
    ...rest
}) => {
    // Themes with press physics need an "is being pressed" signal. `:active`
    // covers the mouse, but keyboard activation is unreliable — Enter gives at
    // most a single frame of `:active` — so the state is mirrored onto
    // `data-ds-pressed`, which the theme CSS also keys off.
    const [pressed, setPressed] = useState(false);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLButtonElement>) => {
            if (e.key === 'Enter' || e.key === ' ') setPressed(true);
            onKeyDown?.(e);
        },
        [onKeyDown],
    );

    const handleKeyUp = useCallback(
        (e: React.KeyboardEvent<HTMLButtonElement>) => {
            setPressed(false);
            onKeyUp?.(e);
        },
        [onKeyUp],
    );

    // Losing focus mid-press (a click that opened a dialog) would otherwise
    // leave the button stuck down.
    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLButtonElement>) => {
            setPressed(false);
            onBlur?.(e);
        },
        [onBlur],
    );

    return (
        <button
            type="button"
            className={`ds-btn ${className}`}
            data-ds-variant={VARIANT[variant]}
            data-ds-tone={color}
            data-ds-size={SIZE[size]}
            data-ds-full={fullWidth ? 'true' : undefined}
            data-ds-pressed={pressed ? 'true' : undefined}
            disabled={disabled}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            onBlur={handleBlur}
            {...rest}
        >
            {startIcon && (
                <span className="flex shrink-0 items-center [&>svg]:text-[1.1em]">{startIcon}</span>
            )}
            {children}
            {endIcon && (
                <span className="flex shrink-0 items-center [&>svg]:text-[1.1em]">{endIcon}</span>
            )}
        </button>
    );
};
export default ButtonUI;
